import {
  incrementProfileProperty,
  logEvent,
  setProfileProperties,
  trackEvent,
} from "@/utils/analytics";
import { hasWon, progressKey, scoreLabel } from "./game";
import { DailyClue, DailyGuess, DailyPuzzle } from "./types";

// Analytics for Daily Service. Every event carries game_mode: "daily" (the
// classic game's carry "classic") and which puzzle it was. Previewed drafts
// are not tracked.

type Properties = Record<string, string | number | boolean | null>;

const track = (puzzle: DailyPuzzle, event: string, properties: Properties = {}) => {
  if (puzzle.draft) return;

  trackEvent(event, {
    game_mode: "daily",
    puzzle_date: puzzle.date,
    origin_airport: puzzle.origin.iata,
    destination_airport: puzzle.destination.iata,
    ...properties,
  });
};

// When the first guess went in, so the finished game can say how long it took.
const startedKey = (puzzle: DailyPuzzle) => `fg_daily_started_${progressKey(puzzle)}`;

const result = (guesses: DailyGuess[]): Properties => ({
  won: hasWon(guesses),
  guesses_used: guesses.length,
  score: scoreLabel(guesses),
});

// The puzzle came up on screen. Fires on every visit, including a return to
// a game already under way or finished.
export const trackPuzzleViewed = (puzzle: DailyPuzzle, guesses: DailyGuess[], finished: boolean) =>
  track(puzzle, "daily_puzzle_viewed", {
    guesses_so_far: guesses.length,
    already_finished: finished,
  });

export const trackHowToPlayViewed = (puzzle: DailyPuzzle, trigger: "first_visit" | "help_button") =>
  track(puzzle, "how_to_play_viewed", { trigger });

// A guess went in. `guesses` includes it. The first guess also starts the
// game, and the last one completes it.
export const trackGuess = (puzzle: DailyPuzzle, guesses: DailyGuess[], finished: boolean) => {
  if (puzzle.draft) return;
  const guess = guesses[guesses.length - 1];

  if (guesses.length === 1) {
    localStorage.setItem(startedKey(puzzle), String(Date.now()));
    track(puzzle, "game_started");
    logEvent("daily_game_start");
    incrementProfileProperty("daily_games_started");
  }

  track(puzzle, "guess_submitted", {
    guess: guess.airport.iata,
    answer: puzzle.destination.iata,
    type: "destination",
    is_correct: guess.band === "correct",
    guess_number: guesses.length,
    distance_miles: Math.round(guess.distanceMiles),
    band: guess.band,
  });
  logEvent("daily_submit_guess");

  if (!finished) return;

  const started = Number(localStorage.getItem(startedKey(puzzle)));
  const misses = guesses.filter((entry) => entry.band !== "correct");
  track(puzzle, "game_completed", {
    ...result(guesses),
    clues_revealed: Math.min(misses.length, puzzle.clues.length),
    closest_miss_miles: misses.length
      ? Math.round(Math.min(...misses.map((entry) => entry.distanceMiles)))
      : null,
    duration: started ? Math.round((Date.now() - started) / 1000) : null,
  });
  logEvent("daily_game_complete");
  incrementProfileProperty("daily_games_completed");
  if (hasWon(guesses)) incrementProfileProperty("daily_games_won");
  setProfileProperties({
    last_daily_puzzle_date: puzzle.date,
    last_daily_score: scoreLabel(guesses),
  });
};

// A clue was tapped to read its detail.
export const trackClueOpened = (puzzle: DailyPuzzle, clue: DailyClue, number: number) =>
  track(puzzle, "clue_opened", { clue_number: number, clue_type: clue.type });

// An earlier guess's pin was tapped to see its distance again.
export const trackGuessReviewed = (puzzle: DailyPuzzle, guess: DailyGuess) =>
  track(puzzle, "guess_reviewed", { guess: guess.airport.iata });

export const trackResultShared = (
  puzzle: DailyPuzzle,
  guesses: DailyGuess[],
  method: "share_sheet" | "copy"
) => {
  track(puzzle, "result_shared", { ...result(guesses), method });
  logEvent("daily_result_shared");
  incrementProfileProperty("daily_results_shared");
};

// The end screen's link to the classic game was followed.
export const trackClassicClicked = (puzzle: DailyPuzzle, guesses: DailyGuess[]) =>
  track(puzzle, "classic_game_clicked", { ...result(guesses), from: "daily_end_screen" });

// The "No flight today" screen has no puzzle to name.
export const trackEmptyClassicClicked = () =>
  trackEvent("classic_game_clicked", { game_mode: "daily", from: "daily_empty_screen" });
